import { Placement } from "@floating-ui/react";

import { loadEnv } from "@smart/bridge-env-dom";
import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import { useToast } from "@smart/bridge-intake-components-dom";
import { familyProQuestionnaireFormUri } from "@smart/bridge-types-basic";
import { ContextMenu, Tooltip } from "@smart/itops-sb-design-system-dom";
import { Region } from "@smart/itops-utils-basic";
import {
  MatterSubmission,
  submissionLink,
  useCopySubmissionLink,
} from "@smart/manage-gql-client-dom";
import { useUser } from "@smart/manage-hooks-dom";

import { useCreateDocFromSubmission } from "./create-doc-from-submission";
import { copySupportInformation } from "../copy-support-information";
import { reminderActionLabel } from "../submission-actions/reminder-helpers";

type ActionsProps = {
  toggleSyncStatus?: () => void;
  onView?: (matterSubmission: MatterSubmission) => void;
  onEdit?: (matterSubmission: MatterSubmission) => void;
  onRemind: (matterSubmission: MatterSubmission) => void;
  onCancel: (matterSubmission: MatterSubmission) => void;
  onDelete: (matterSubmission: MatterSubmission) => void;
  onGenerateQRCode: (matterSubmission: MatterSubmission) => void;
  matterSubmission?: MatterSubmission;
  tooltipPlacement?: Placement;
  tooltipText?: string;
  id?: string;
  className?: string;
  variant?: "default" | "subtle";
  size?: "default" | "small";
};

const isInternalUse = (submission: MatterSubmission) =>
  submission.status === "draft" &&
  submission.rawSubmission.values?.communicationMethod === "internalUse";

export const ActionsMenu = ({
  toggleSyncStatus,
  id,
  onView,
  onEdit,
  onRemind,
  onCancel,
  onDelete,
  onGenerateQRCode,
  matterSubmission,
  tooltipPlacement,
  tooltipText,
  className,
  variant,
  size,
}: ActionsProps) => {
  const [copySubmissionLink] = useCopySubmissionLink();
  const { setToasts } = useToast();
  const { user } = useUser();
  const { wordGenerationIntake, "LINK-3736": automaticRemindersFlag } =
    useFeatureFlags();
  const { createDocument, createFormDocument, createLetterDocument } =
    useCreateDocFromSubmission(matterSubmission);
  const onAction = (action: (() => void | Promise<void>) | undefined) => () =>
    action?.();

  if (!matterSubmission) return null;

  const isUsRegion = (loadEnv("Region") as Region) === "nova";

  const isActionable =
    matterSubmission.status === "active" || isInternalUse(matterSubmission);

  const isFormDeleted = !!matterSubmission.rawSubmission.form?.values?.deleted;

  const isFamilyProQuestionnaire =
    matterSubmission.formUri === familyProQuestionnaireFormUri;

  const onCopyLink = (submission: MatterSubmission) => {
    const setToast = () => {
      setToasts([
        {
          text: "Link successfully copied",
          width: "small",
        },
      ]);
    };
    if (submission.formUri === familyProQuestionnaireFormUri) {
      setToast();
    } else {
      copySubmissionLink({ variables: { uri: submission.uri } })
        .then(setToast)
        .catch((err) => console.error(err));
    }
  };

  const items = [
    {
      label: "View",
      onClick: onAction(() => onView && onView(matterSubmission)),
      hidden: isFamilyProQuestionnaire || !onView || isFormDeleted,
    },
    {
      label: reminderActionLabel(
        isInternalUse(matterSubmission),
        !!automaticRemindersFlag,
        "Send Reminder",
      ),
      onClick: onAction(() => onRemind(matterSubmission)),
      hidden: !isActionable || isFormDeleted,
    },
    {
      label: "Copy Link",
      onClick: onAction(async () => {
        const link = isFamilyProQuestionnaire
          ? matterSubmission.externalSubmissionLink
          : submissionLink(matterSubmission.rawSubmission);
        if (link) {
          await navigator.clipboard.writeText(link);
          onCopyLink(matterSubmission);
        }
      }),
      hidden:
        (!isActionable && !isFamilyProQuestionnaire) ||
        (isFamilyProQuestionnaire && matterSubmission.status === "draft") ||
        isFormDeleted,
    },
    {
      label: "Resend",
      onClick: onAction(() => onRemind(matterSubmission)),
      hidden:
        !isFamilyProQuestionnaire ||
        matterSubmission.status !== "completed" ||
        isFormDeleted,
    },
    {
      label: "Show QR Code",
      onClick: onAction(() => onGenerateQRCode(matterSubmission)),
      hidden: matterSubmission.status !== "active" || isFormDeleted,
    },
    {
      label: "Edit Responses",
      onClick: onAction(() => onEdit && onEdit(matterSubmission)),
      hidden: !isActionable || !onEdit || isFormDeleted,
      disabled: matterSubmission.syncStatus === "pendingContactReview",
    },
    {
      label: "Open in Word",
      hidden:
        !wordGenerationIntake ||
        matterSubmission.status !== "completed" ||
        (!createDocument && !createFormDocument && !createLetterDocument) ||
        isFormDeleted,
      subMenu: [
        {
          label: "Blank",
          onClick: onAction(createDocument),
          hidden: !createDocument,
        },
        {
          label: "Form",
          onClick: onAction(createFormDocument),
          hidden: !isUsRegion || !createFormDocument,
        },
        {
          label: "Letter",
          onClick: onAction(createLetterDocument),
          hidden: !createLetterDocument,
        },
      ],
    },
    {
      label: "Close Form",
      onClick: onAction(() => onCancel(matterSubmission)),
      hidden: !isActionable || isFamilyProQuestionnaire || isFormDeleted,
    },
    {
      label: "Delete Form",
      onClick: onAction(() => onDelete(matterSubmission)),
      hidden: !isActionable || isFamilyProQuestionnaire,
    },
    {
      label: "Copy support information",
      onClick: () => {
        copySupportInformation({
          teamUri: user?.teamUri,
          formUri: matterSubmission.formUri,
          userUri: user?.userUri,
          submissionUri: matterSubmission.uri,
        });
      },
      internal: true,
    },
    {
      label: "Show Sync Status",
      onClick: toggleSyncStatus,
      hidden: !toggleSyncStatus,
      internal: true,
    },
  ];

  return (
    <Tooltip placement={tooltipPlacement} text={tooltipText} offsetBy={10}>
      <ContextMenu
        button={{
          className:
            size === "small" ? "w-[2rem] h-[2rem]" : "w-[4rem] h-[4rem]",
          variant:
            variant === "subtle" ? "secondarySubtle" : "secondaryDefault",
          leftIcon: "solidEllipsis",
          dataTestId: "submission-actions-menu-button",
          disabled: items
            .filter((item) => !item.internal)
            .every((item) => item.hidden),
        }}
        placement="bottom-end"
        offsetBy={5}
        items={items}
        id={id}
        className={className}
      />
    </Tooltip>
  );
};
